import React, { useState, useEffect } from 'react';
import { getOrders } from '../../api/client';

function fmtTime(raw) {
  if (!raw || raw === '--') return '--';
  try {
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
    const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
    return `${date} ${time}`;
  } catch {
    return raw;
  }
}

const FYERS_STATUS = {
  2: ['COMPLETE',  'badge-green'],
  6: ['CANCELLED', 'badge-grey'],
  5: ['REJECTED',  'badge-red'],
  1: ['PENDING',   'badge-yellow'],
};

const PAPER_STATUS = {
  OPEN:      ['OPEN',      'badge-yellow'],
  CLOSED:    ['CLOSED',    'badge-green'],
  CANCELLED: ['CANCELLED', 'badge-grey'],
};

function getStatusBadge(o) {
  if (o.paper_trade) {
    return PAPER_STATUS[o.status] || [String(o.status || 'UNKNOWN'), 'badge-grey'];
  }
  return FYERS_STATUS[o.status] || [String(o.status || 'UNKNOWN'), 'badge-grey'];
}

function getSide(o) {
  if (o.paper_trade) return o.side || 'BUY';
  return o.side === 1 ? 'BUY' : 'SELL';
}

function normalise(o) {
  const [statusLabel, statusCls] = getStatusBadge(o);
  const side      = getSide(o);
  const time      = fmtTime(o.orderDateTime || o.entryTime || '--');
  const exitTime  = fmtTime(o.exitTime || null);
  const price     = o.tradedPrice || o.limitPrice || o.avgPrice || o.price || 0;
  const exitPrice = o.exitPrice != null ? o.exitPrice : null;
  const orderId   = o.id || o.orderId || '--';
  const type      = o.type || o.orderType || (o.paper_trade ? 'PAPER' : '--');
  const qty       = o.qty || o.quantity || 0;
  const pnl       = o.paper_trade ? (o.pnl || 0) : null;
  return { side, statusLabel, statusCls, time, exitTime, price, exitPrice, orderId, type, qty, pnl };
}

export default function OrderLogs() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchOrders();
    const t = setInterval(fetchOrders, 10000);
    return () => clearInterval(t);
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await getOrders();
      setOrders(res.data.orders || []);
    } catch {}
  };

  const exportCSV = () => {
    const headers = ['Entry Time', 'Exit Time', 'Symbol', 'Side', 'Type', 'Qty', 'Entry Price', 'Exit Price', 'PnL', 'Status', 'Order ID'];
    const rows = orders.map(o => {
      const n = normalise(o);
      const pnlStr = n.pnl !== null ? n.pnl.toFixed(2) : '';
      const exitPriceStr = n.exitPrice != null ? Number(n.exitPrice).toFixed(2) : '';
      return [n.time, n.exitTime || '', o.symbol, n.side, n.type, n.qty,
              Number(n.price).toFixed(2), exitPriceStr, pnlStr, n.statusLabel, n.orderId];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'orders.csv'; a.click();
  };

  const filtered = filter
    ? orders.filter(o => o.symbol && o.symbol.toLowerCase().includes(filter.toLowerCase()))
    : orders;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Order Logs</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            placeholder="Filter by symbol..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: 200 }}
          />
          <button className="btn btn-ghost" onClick={exportCSV}>Export CSV</button>
        </div>
      </div>

      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Entry Time</th><th>Symbol</th><th>Strategy</th>
              <th>Side</th><th>Type</th><th>Qty</th>
              <th>Entry Price</th><th>Exit Price</th><th>P&amp;L</th><th>Status</th><th>Order ID</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={11} style={{ color: 'var(--muted)', textAlign: 'center', padding: 24 }}>
                  No orders found
                </td>
              </tr>
            ) : filtered.map((o, i) => {
              const n = normalise(o);
              const isClosed = o.status === 'CLOSED';
              const pnlColor = n.pnl === null ? 'var(--muted)' : n.pnl >= 0 ? 'var(--green)' : 'var(--red)';
              const pnlText  = n.pnl === null ? '--'
                : (n.pnl >= 0 ? '+' : '') + '₹' + n.pnl.toFixed(2);
              return (
                <tr key={i}>
                  <td style={{ fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                    {n.time}
                    {isClosed && n.exitTime && n.exitTime !== '--' && (
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, opacity: 0.7 }}>
                        Exit: {n.exitTime}
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: 500 }}>{o.symbol}</td>
                  <td style={{ fontSize: 11, color: 'var(--muted)' }}>{o.strategy || '--'}</td>
                  <td>
                    <span className={`badge ${n.side === 'BUY' ? 'badge-green' : 'badge-red'}`}>
                      {n.side}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{n.type}</td>
                  <td>{n.qty}</td>
                  <td>₹{Number(n.price).toFixed(2)}</td>
                  <td style={{ color: isClosed && n.exitPrice != null ? 'var(--text)' : 'var(--muted)' }}>
                    {n.exitPrice != null ? `₹${Number(n.exitPrice).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ fontWeight: 600, color: pnlColor }}>{pnlText}</td>
                  <td>
                    <span className={`badge ${n.statusCls}`}>{n.statusLabel}</span>
                  </td>
                  <td style={{ fontSize: 10, color: 'var(--muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {n.orderId}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
